import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database, RefreshCw, Download, RotateCcw, Trash2, Save, Mail,
  Cloud, Lock, Unlock, Clock, CheckCircle, XCircle, AlertTriangle,
  Settings, History, Plus, X, ChevronDown, ChevronUp, Loader2, Shield,
  Globe, Bell
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Badge from '../components/common/Badge';
import Skeleton from '../components/common/Skeleton';
import EmptyState from '../components/common/EmptyState';
import BackupStatus from '../components/backup/BackupStatus';
import BackupHistoryList from '../components/backup/BackupHistoryList';
import {
  createBackup, fetchBackups, deleteBackup, restoreBackup,
  getBackupSettings, updateBackupSettings, getBackupStatus,
  testEmail, testCloud, getBackupSchedule, updateBackupSchedule,
  downloadBackup
} from '../redux/slices/backupSlice';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', damping: 20, stiffness: 200 } },
};

const cloudProviders = [
  { value: 'none', label: 'None' },
  { value: 'aws', label: 'Amazon AWS' },
  { value: 'google', label: 'Google Cloud' },
  { value: 'azure', label: 'Microsoft Azure' },
];

export default function Backup() {
  const dispatch = useDispatch();
  const { backups, status, settings, schedule, loading, error } = useSelector(
    (state) => state.backups
  );

  const [creatingBackup, setCreatingBackup] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testCloudLoading, setTestCloudLoading] = useState(false);
  const [saveSettingsLoading, setSaveSettingsLoading] = useState(false);

  const [autoBackup, setAutoBackup] = useState(false);
  const [backupInterval, setBackupInterval] = useState(30);
  const [emailBackup, setEmailBackup] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [cloudProvider, setCloudProvider] = useState('none');
  const [encryption, setEncryption] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');

  useEffect(() => {
    dispatch(fetchBackups({ page: 1, limit: 50 }));
    dispatch(getBackupSettings());
    dispatch(getBackupStatus());
    dispatch(getBackupSchedule());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      setAutoBackup(settings.autoBackup || false);
      setBackupInterval(settings.interval || 30);
      setEmailBackup(settings.emailBackup || false);
      setEmailRecipients(settings.emailRecipients || []);
      setCloudProvider(settings.cloudProvider || 'none');
      setEncryption(settings.encryption || false);
    }
  }, [settings]);

  const handleCreateBackup = useCallback(async () => {
    setCreatingBackup(true);
    try {
      await dispatch(createBackup({})).unwrap();
      toast.success('Backup created successfully');
      dispatch(fetchBackups({ page: 1, limit: 50 }));
    } catch (err) {
      toast.error(err?.message || 'Backup failed');
    } finally {
      setCreatingBackup(false);
    }
  }, [dispatch]);

  const handleDeleteBackup = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteBackup(deleteTarget._id || deleteTarget.id)).unwrap();
      setDeleteTarget(null);
      toast.success('Backup deleted');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete backup');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      await dispatch(restoreBackup({ id: restoreTarget._id || restoreTarget.id, options: {} })).unwrap();
      setRestoreTarget(null);
      toast.success('Backup restored successfully');
    } catch (err) {
      toast.error(err?.message || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const handleDownload = (backup) => {
    dispatch(downloadBackup(backup._id || backup.id));
    toast.success('Downloading backup...');
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    try {
      await dispatch(testEmail()).unwrap();
      toast.success('Email test successful');
    } catch (err) {
      toast.error(err?.message || 'Email test failed');
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleTestCloud = async () => {
    setTestCloudLoading(true);
    try {
      await dispatch(testCloud()).unwrap();
      toast.success('Cloud connection successful');
    } catch (err) {
      toast.error(err?.message || 'Cloud test failed');
    } finally {
      setTestCloudLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaveSettingsLoading(true);
    try {
      await dispatch(updateBackupSettings({
        autoBackup,
        interval: backupInterval,
        emailBackup,
        emailRecipients,
        cloudProvider,
        encryption,
        backupPassword: encryption ? backupPassword : undefined,
      })).unwrap();
      toast.success('Backup settings saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save settings');
    } finally {
      setSaveSettingsLoading(false);
    }
  };

  const addEmail = () => {
    if (!newEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
      toast.error('Invalid email address');
      return;
    }
    if (emailRecipients.includes(newEmail.trim())) {
      toast.error('Email already added');
      return;
    }
    setEmailRecipients((prev) => [...prev, newEmail.trim()]);
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setEmailRecipients((prev) => prev.filter((e) => e !== email));
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 md:p-6 space-y-6"
    >
      <PageHeader
        title="Backup System"
        description="Manage database backups, schedules, and settings"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div variants={itemVariants} className="lg:col-span-1">
          <BackupStatus
            lastBackup={status?.lastBackup || settings?.lastBackup}
            nextBackup={status?.nextBackup || schedule?.nextBackup}
            status={creatingBackup ? 'running' : status?.status || (status?.lastBackup ? 'success' : 'failed')}
            progress={status?.progress || 0}
            onBackupNow={handleCreateBackup}
            loading={creatingBackup}
          />
        </motion.div>

        <motion.div variants={itemVariants} className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Settings size={18} className="text-gray-400" />
                Backup Settings
              </h3>
            </div>

            <div className="px-6 py-5 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Auto Backup</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Automatically create backups on schedule</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={autoBackup} onChange={(e) => setAutoBackup(e.target.checked)} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                </label>
              </div>

              {autoBackup && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Backup Interval (minutes)</label>
                    <select
                      value={backupInterval}
                      onChange={(e) => setBackupInterval(parseInt(e.target.value))}
                      className="px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 w-full max-w-xs"
                    >
                      <option value={5}>Every 5 minutes</option>
                      <option value={10}>Every 10 minutes</option>
                      <option value={15}>Every 15 minutes</option>
                      <option value={30}>Every 30 minutes</option>
                      <option value={60}>Every 60 minutes</option>
                    </select>
                  </div>
                </motion.div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Email Backup</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Send backup files via email</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={emailBackup} onChange={(e) => setEmailBackup(e.target.checked)} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                  </label>
                </div>
                {emailBackup && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-3"
                  >
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                        placeholder="Enter email address"
                        className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                      />
                      <Button variant="secondary" size="sm" icon={Plus} onClick={addEmail}>
                        Add
                      </Button>
                    </div>
                    {emailRecipients.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {emailRecipients.map((email) => (
                          <Badge key={email} variant="info" size="md" onClose={() => removeEmail(email)}>
                            <Mail size={12} className="mr-1" />
                            {email}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Cloud Provider</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Store backups in the cloud</p>
                  </div>
                </div>
                <select
                  value={cloudProvider}
                  onChange={(e) => setCloudProvider(e.target.value)}
                  className="px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 w-full max-w-xs"
                >
                  {cloudProviders.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Encryption</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Encrypt backup files with password</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={encryption} onChange={(e) => setEncryption(e.target.checked)} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                  </label>
                </div>
                {encryption && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3"
                  >
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="password"
                        value={backupPassword}
                        onChange={(e) => setBackupPassword(e.target.value)}
                        placeholder="Enter backup encryption password"
                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" icon={Mail} onClick={handleTestEmail} loading={testEmailLoading}>
                  Test Email
                </Button>
                {cloudProvider !== 'none' && (
                  <Button variant="secondary" size="sm" icon={Globe} onClick={handleTestCloud} loading={testCloudLoading}>
                    Test Cloud
                  </Button>
                )}
              </div>
              <Button variant="primary" size="sm" icon={Save} onClick={handleSaveSettings} loading={saveSettingsLoading}>
                Save Settings
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div variants={itemVariants}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <History size={18} className="text-gray-400" />
            Backup History
          </h3>
        </div>
        <BackupHistoryList
          backups={backups}
          loading={loading}
          onDownload={handleDownload}
          onRestore={(b) => setRestoreTarget(b)}
          onDelete={(b) => setDeleteTarget(b)}
        />
      </motion.div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteBackup}
        title="Delete Backup"
        message="Are you sure you want to delete this backup? This action cannot be undone."
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />

      <ConfirmDialog
        isOpen={!!restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleRestoreBackup}
        title="Restore Backup"
        message="Are you sure you want to restore this backup? This will overwrite current data."
        confirmLabel="Restore"
        loading={restoring}
        variant="warning"
      />
    </motion.div>
  );
}
